import { useState, useEffect } from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { useSaleCreateStore } from "../../stores/useSaleCreateStore"

export const TermsConditionsStep = () => {
  const { saleData, updateTerms } = useSaleCreateStore()

  const [biddingTerms, setBiddingTerms] = useState(saleData.bidding_tc || "")
  const [contractTerms, setContractTerms] = useState(saleData.contract_tc || "")
  const [activeTab, setActiveTab] = useState<"bidding" | "contract">("bidding")

  // Update store whenever terms change
  useEffect(() => {
    updateTerms(biddingTerms, contractTerms)
  }, [biddingTerms, contractTerms, updateTerms])

  const currentText = activeTab === "bidding" ? biddingTerms : contractTerms
  const setCurrentText =
    activeTab === "bidding" ? setBiddingTerms : setContractTerms

  const SAMPLE_BIDDING_TERMS = `## Bidding Instructions

1. **Bid Submission**: All bids must be submitted through the NCX platform before the specified deadline.

2. **Bid Format**: 
   - For lump sum sales: Submit a single total amount
   - For pay-as-cut sales: Submit per-unit prices for each timber product

3. **Acceptance**: The seller reserves the right to reject any and all bids and to select the successful bidder at their discretion.

4. **Minimum Bids**: No minimum bid amount specified.

## Important Notes

- Late bids will not be accepted
- All bids are binding once submitted
- Questions should be directed to the sale contact prior to bidding`

  const SAMPLE_CONTRACT_TERMS = `## Contract Terms and Conditions

### Performance Requirements

1. **Performance Bond**: A performance bond of $3,000 must be deposited into an NCX non-interest bearing Forestry Escrow Account upon contract signing.

2. **Harvest Timeline**: All timber must be harvested within 12 months of contract execution.

3. **Quality Standards**: Logging operations must maintain professional standards with minimal soil disturbance.

### Payment Terms

- **Lump Sum**: Full payment due within 30 days of contract execution
- **Pay as Cut**: Payment due monthly based on harvested volumes

### Environmental Compliance

- Comply with all applicable federal, state, and local environmental regulations
- Maintain appropriate buffer zones near water sources
- Follow best management practices for forestry operations

### Default and Remedies

Failure to comply with contract terms may result in forfeiture of performance bond and contract termination.`

  const insertSampleText = () => {
    if (activeTab === "bidding") {
      setBiddingTerms(SAMPLE_BIDDING_TERMS)
    } else {
      setContractTerms(SAMPLE_CONTRACT_TERMS)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Terms and Conditions
        </h3>
        <p className="text-gray-600 mb-4">
          Define the bidding and contract terms for this sale. You can write in
          Markdown format for better formatting.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("bidding")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "bidding"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Bidding Terms
          </button>
          <button
            onClick={() => setActiveTab("contract")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === "contract"
                ? "border-blue-500 text-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Contract Terms
          </button>
        </nav>
      </div>

      {/* Sample Text Button */}
      <div className="flex justify-end">
        <button
          onClick={insertSampleText}
          className="text-sm text-blue-600 hover:text-blue-800 underline"
        >
          Insert sample {activeTab} terms
        </button>
      </div>

      {/* Editor/Preview Area */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Editor */}
        <div>
          <div className="mb-2">
            <label className="block text-sm font-medium text-gray-700">
              {activeTab === "bidding"
                ? "Bidding Terms & Conditions"
                : "Contract Terms & Conditions"}
            </label>
          </div>
          <textarea
            value={currentText}
            onChange={(e) => setCurrentText(e.target.value)}
            rows={16}
            className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
            placeholder={`Enter ${activeTab} terms and conditions here. You can use Markdown formatting:\n\n## Headers\n**Bold text**\n- Bullet points\n1. Numbered lists\n\nClick "Insert sample terms" to get started with a template.`}
          />
          <p className="text-xs text-gray-500 mt-1">
            Supports Markdown formatting. Use ## for headers, **bold**, - for
            lists, etc.
          </p>
        </div>

        {/* Preview */}
        <div>
          <div className="mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Preview
            </label>
          </div>
          <div className="border border-gray-300 rounded-md p-3 bg-white min-h-[400px] max-h-[400px] overflow-y-auto">
            {currentText ? (
              <div className="prose prose-sm max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {currentText}
                </ReactMarkdown>
              </div>
            ) : (
              <p className="text-gray-500 text-sm italic">
                Preview will appear here as you type...
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Markdown Help */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">
          Markdown Quick Reference
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-blue-800">
          <div>
            <p>
              <code>## Header</code> - Section header
            </p>
            <p>
              <code>**Bold**</code> - Bold text
            </p>
            <p>
              <code>*Italic*</code> - Italic text
            </p>
          </div>
          <div>
            <p>
              <code>- Item</code> - Bullet point
            </p>
            <p>
              <code>1. Item</code> - Numbered list
            </p>
            <p>
              <code>[Link](url)</code> - Hyperlink
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
